import services from 'services';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { setCard, setCardByToken } from 'store/card/cybersource.slice';
import { RootState } from 'types/store';
import { setOptions } from 'store/card/options';

type setPaymentMethodArgs = {
    cardToken?: CardToken;
};

export const setPaymentMethod = createAsyncThunk(
    'cybersource/setPayment',
    async ({ cardToken }: setPaymentMethodArgs, { getState }) => {
        const state = getState() as RootState;

        if (state.cart.data == null || state.billing.data == null)
            throw new Error('Datos incompletos para usar Cybersource');

        const payload = getPaymentPayload(state.cart.data, state.billing.data, cardToken);
        const response = await services.cart.setPayment(state.cart.data.id, payload);
        const payment =
            response.paymentInfo.find((payment) => payment.paymentMethod == 'Cybersource') || null;

        if (!payment?.id || payment?.customFields == null)
            throw new Error('El medio de pago no pudo ser configurado');

        return {
            id: payment.id,
            jwk: payment.customFields.jwk,
            cart: response.cart,
        };
    },
);

function getPaymentPayload(
    cart: Cart,
    billing: PartialPaymentInfo,
    card?: CardToken,
): PaymentInfo[] {
    const price = cart.grandTotal['discount'] || cart.grandTotal['master'];

    const payload = [
        {
            documentType: billing.documentType,
            paymentMethod: 'Cybersource' as PaymentMethodName,
            type: 'TOKENIZATION',
            billingInfo: {
                business: billing.billingInfo.business,
                customer: billing.billingInfo.customer,
                address: billing.billingInfo.address,
            },
            amount: price,
            customFields: {},
        },
    ];

    if (card) {
        payload[0]['cardId'] = card.id;
        payload[0]['type'] = 'BUY';
    }

    return payload;
}

export type CybersourceData = {
    id: string;
    jwk: string;
    isTokenized: boolean;
    card: null | {
        installments: number;
        deferred: number;
    };
};

export type CybersourceState = {
    status: 'idle' | 'pending' | 'ok' | 'error';
    data: CybersourceData | null;
    error: SerializedError | null;
};

const initialState: CybersourceState = {
    status: 'idle',
    data: null,
    error: null,
};

export type SetPaymentByTokenArg = {
    card: CardToken;
    cvv: string;
};

export default createSlice({
    name: 'cybersource',
    initialState: initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(setPaymentMethod.pending, (state: CybersourceState) => {
            state.status = 'pending';
            state.error = null;
            state.data = null;
        });

        builder.addCase(
            setPaymentMethod.fulfilled,
            (state: CybersourceState, { payload, meta }) => {
                state.status = 'ok';
                state.error = null;
                state.data = {
                    id: payload.id,
                    jwk: payload.jwk,
                    card: null,
                    isTokenized: meta.arg.cardToken?.id ? true : false,
                };
            },
        );

        builder.addCase(setPaymentMethod.rejected, (state: CybersourceState, action) => {
            state.status = 'error';
            state.data = null;
            state.error = action.error;
        });

        builder.addCase(setCard.pending, (state: CybersourceState) => {
            state.status = 'pending';
        });
        builder.addCase(setCard.rejected, (state: CybersourceState, { error }) => {
            state.status = 'error';
            state.error = error;
        });

        builder.addCase(setCard.fulfilled, (state: CybersourceState, { payload }) => {
            state.status = 'ok';
            if (state.data) state.data = { ...state.data, card: payload };
            state.error = null;
        });

        builder.addCase(setCardByToken.pending, (state: CybersourceState) => {
            state.status = 'pending';
        });
        builder.addCase(setCardByToken.rejected, (state: CybersourceState) => {
            state.status = 'error';
        });
        builder.addCase(setCardByToken.fulfilled, (state: CybersourceState) => {
            state.status = 'ok';
        });

        builder.addCase(setOptions.fulfilled, (state: CybersourceState, { meta }) => {
            if (meta.arg.paymentMethod === 'Cybersource') {
                if (state.data?.card) {
                    state.data.card.installments = meta.arg.installments ?? 1;
                }
            }
        });
    },
});
